import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ColumnDef {
  key: string;
  label: string;
}

export interface ReportSchemaGroup {
  key: string;
  label: string;
  /** false para columnas numéricas/fecha: el filtro de texto del grid no aplica. */
  filterable?: boolean;
}

export interface ReportSchema {
  clients: ReportSchemaGroup[];
  projects: ReportSchemaGroup[];
  collections: ReportSchemaGroup[];
  reconciliations: ReportSchemaGroup[];
}

/**
 * Filtros generales del reporte (contrato validado en el backend).
 * - status/active: estado ACTUAL del cliente; OR dentro de cada grupo, AND entre grupos.
 * - collection_from/collection_to: días de calendario YYYY-MM-DD (America/New_York en el backend);
 *   solo afectan a Collection Data. No confundir con created_from/created_to (alta del cliente).
 */
export interface ReportFilters {
  status?: string[];
  active?: string[];
  collection_from?: string;
  collection_to?: string;
  [legacyKey: string]: any;
}

export interface ReportConfig {
  _id?: string;
  name: string;
  isShared: boolean;
  baseEntity: string;
  selectedColumns: string[];
  filters: ReportFilters;
  sortBy?: { field: string; direction: 'asc' | 'desc' };
  createdAt?: string;
}

export interface ColumnFilter {
  column: string;     // binding key del grid (ej: 'client_name')
  value: string;
  operator: 'contains' | 'eq' | 'startsWith' | 'endsWith';
}

export interface ReportQueryPayload {
  selectedColumns: string[];
  filters: ReportFilters;
  columnFilters?: ColumnFilter[];
  page: number;
  limit: number;
  sortBy?: { field: string; direction: 'asc' | 'desc' };
}

export interface ReportQueryResult {
  ok: boolean;
  data: any[];
  total: number;
  columns: ColumnDef[];
}

export interface FinancialAnalysisPayload {
  months: string[];
  salesperson_id?: string;
  city?: string;
  zone?: string;
  zip?: string;
}

export interface FinancialAnalysisRow {
  client_id?: string;
  client_name: string;
  address: string;
  is_signed?: string;
  salesperson: string;
  contact?: string;
  reconciliation_type: string;
  month: string;
  gross_gallons: number;
  clean_gallons: number;
  waste_gallons: number;
  cost: number;
  payout: number;
}

export interface FinancialAnalysisResult {
  ok: boolean;
  data: FinancialAnalysisRow[];
  total: number;
}


@Injectable({ providedIn: 'root' })
export class CustomReportService {
  private baseUrl = `${environment.base_url}/custom-reports`;

  constructor(private http: HttpClient) { }

  private get headers() {
    return {
      headers: {
        'Content-Type': 'application/json',
        'x-token': localStorage.getItem('token') || ''
      }
    };
  }

  /** Obtener el catálogo de campos disponibles por entidad */
  getSchema(): Observable<{ ok: boolean; schema: ReportSchema }> {
    return this.http.get<any>(`${this.baseUrl}/schema`, this.headers);
  }

  /** Ejecutar un reporte dinámico paginado */
  runQuery(payload: ReportQueryPayload): Observable<ReportQueryResult> {
    return this.http.post<ReportQueryResult>(`${this.baseUrl}/query`, payload, this.headers);
  }

  /** Exportar a Excel — devuelve Observable<Blob> para que el componente maneje el feedback */
  exportExcel(payload: Omit<ReportQueryPayload, 'page' | 'limit'>): Observable<Blob> {
    const token = localStorage.getItem('token') || '';
    const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'x-token': token });

    return this.http.post(`${this.baseUrl}/export-excel`, payload, {
      headers,
      responseType: 'blob'
    });
  }

  /** Listar configuraciones guardadas del usuario */
  getConfigs(): Observable<{ ok: boolean; configs: ReportConfig[] }> {
    return this.http.get<any>(`${this.baseUrl}/configs`, this.headers);
  }

  /** Guardar una configuración de reporte */
  saveConfig(config: Omit<ReportConfig, '_id' | 'createdAt'>): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/configs`, config, this.headers);
  }

  /** Eliminar una configuración guardada */
  deleteConfig(id: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/configs/${id}`, this.headers);
  }

  // ── Financial Analysis Report ────────────────────────────────────────────────

  /** Ejecutar reporte de análisis financiero mensual por cliente */
  runFinancialAnalysis(payload: FinancialAnalysisPayload): Observable<FinancialAnalysisResult> {
    return this.http.post<FinancialAnalysisResult>(`${this.baseUrl}/financial-analysis`, payload, this.headers);
  }

  /** Exportar análisis financiero a Excel — devuelve Blob */
  exportFinancialAnalysisExcel(payload: FinancialAnalysisPayload): Observable<Blob> {
    const token = localStorage.getItem('token') || '';
    const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'x-token': token });
    return this.http.post(`${this.baseUrl}/financial-analysis/export`, payload, { headers, responseType: 'blob' });
  }
}

